import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton
} from '@material-ui/core';
import {
  ChevronLeftRounded,
  ChevronRightRounded,
  FullscreenExitRounded as MinimizeIcon,
  CakeRounded as BirthdayIcon,
  WorkOffRounded as GeneralLeaveIcon,
  BeachAccessRounded as PublicHolidayIcon
} from '@material-ui/icons';
import styled from 'styled-components';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { EventType } from 'domain-types';

interface OverviewEvent {
  type: EventType;
  name: string;
  date: string;
  end?: string;
}

interface Props {
  onMinimize: () => void;
  events: OverviewEvent[];
}

const CalendarWrapper = styled(Box)`
  width: 100%;
`;

const MinimizeButton = styled(IconButton)`
  border: 1px solid #e2e8f0;
  padding: 8px;
  background-color: #ffffff;
  color: #64748b;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f8fafc;
    border-color: #cbd5e1;
    color: #0f172a;
    transform: scale(1.05);
  }
`;

const CalendarContainer = styled(Box)`
  padding: 16px 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const HeaderRow = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const MonthNavigation = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MonthLabel = styled(Typography)`
  font-weight: 700;
  color: #0f172a;
  font-size: 1.25rem;
  min-width: 160px;
  text-align: center;
  text-transform: capitalize;
`;

const NavButton = styled(IconButton)`
  border: 1px solid #e2e8f0;
  padding: 8px;
  background-color: #ffffff;
  color: #475569;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f8fafc;
    border-color: #cbd5e1;
    color: #0f172a;
    transform: translateY(-1px);
  }
`;

const TodayButton = styled(Button)`
  border-radius: 8px;
  text-transform: none;
  font-weight: 600;
  font-size: 0.875rem;
  padding: 6px 16px;
  border: 1px solid #e2e8f0;
  color: #475569;
  background-color: #ffffff;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f8fafc;
    border-color: #cbd5e1;
    color: #0f172a;
  }
`;

const WeekdaysGrid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 8px;
  text-align: center;
  margin-bottom: 4px;
`;

const WeekdayCell = styled(Box)`
  font-weight: 600;
  font-size: 0.75rem;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 8px 0;
`;

const DaysGrid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-auto-rows: minmax(110px, 1fr);
  gap: 8px;
`;

interface DayCellProps {
  isCurrentMonth: boolean;
  isToday: boolean;
}

const DayCell = styled(Box)<DayCellProps>`
  background-color: ${p => (p.isToday ? '#f8fafc' : '#ffffff')};
  border: 1px solid ${p => (p.isToday ? '#3b82f6' : '#e2e8f0')};
  border-radius: 12px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  opacity: ${p => (p.isCurrentMonth ? 1 : 0.45)};
  transition: all 0.2s ease-in-out;
  box-shadow: ${p => (p.isToday ? '0 0 0 1px #3b82f6' : 'none')};

  &:hover {
    border-color: ${p => (p.isToday ? '#2563eb' : '#cbd5e1')};
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
    transform: translateY(-2px);
  }
`;

const DayHeader = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

interface DayNumberProps {
  isToday: boolean;
}

const DayNumber = styled(Typography)<DayNumberProps>`
  font-weight: ${p => (p.isToday ? '800' : '600')};
  font-size: 0.875rem;
  color: ${p => (p.isToday ? '#3b82f6' : '#334155')};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${p => (p.isToday ? '#dbeafe' : 'transparent')};
`;

const EventsList = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow-y: auto;
  flex-grow: 1;
  max-height: 80px;

  /* Custom scrollbar for events */
  &::-webkit-scrollbar {
    width: 3px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 2px;
  }
`;

interface EventPillProps {
  eventType: EventType;
}

const EventPill = styled(Box)<EventPillProps>`
  font-size: 0.72rem;
  font-weight: 600;
  padding: 3px 6px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: all 0.15s ease-in-out;
  cursor: default;

  ${p => {
    switch (p.eventType) {
      case 'birthday':
        return `
          background-color: hsl(340, 100%, 97%);
          color: hsl(340, 90%, 45%);
          border-left: 3px solid hsl(340, 90%, 60%);
          &:hover {
            background-color: hsl(340, 100%, 94%);
          }
        `;
      case 'leave':
        return `
          background-color: hsl(24, 100%, 96%);
          color: hsl(24, 90%, 40%);
          border-left: 3px solid hsl(24, 90%, 55%);
          &:hover {
            background-color: hsl(24, 100%, 93%);
          }
        `;
      case 'public-holiday':
        return `
          background-color: hsl(210, 100%, 96%);
          color: hsl(210, 90%, 40%);
          border-left: 3px solid hsl(210, 90%, 55%);
          &:hover {
            background-color: hsl(210, 100%, 93%);
          }
        `;
      default:
        return `
          background-color: hsl(200, 100%, 96%);
          color: hsl(200, 90%, 40%);
          border-left: 3px solid hsl(200, 90%, 55%);
        `;
    }
  }}

  svg {
    font-size: 11px;
    flex-shrink: 0;
  }
`;

export function DashboardCalendar({ onMinimize, events }: Props) {
  const { t } = useTranslation();
  const [currentMonth, setCurrentMonth] = useState(() => moment());

  const handlePrevMonth = () => {
    setCurrentMonth(prev => prev.clone().subtract(1, 'month'));
  };

  const handleNextMonth = () => {
    setCurrentMonth(prev => prev.clone().add(1, 'month'));
  };

  const handleToday = () => {
    setCurrentMonth(moment());
  };

  // Generate localized weekday names dynamically, respecting current locale starting day
  const weekdays = moment.weekdaysMin(true);

  // Generate calendar grid days
  const startDay = currentMonth.clone().startOf('month').startOf('week');
  const endDay = currentMonth.clone().endOf('month').endOf('week');

  const weeks = [];
  const date = startDay.clone();
  while (date.isBefore(endDay) || weeks.length < 6) {
    const days = [];
    for (let i = 0; i < 7; i++) {
      days.push(date.clone());
      date.add(1, 'day');
    }
    weeks.push(days);
    if (date.isAfter(endDay) && weeks.length >= 5) {
      break;
    }
  }

  const isDayInEvent = (dayMoment: moment.Moment, event: OverviewEvent) => {
    const dayStr = dayMoment.format('YYYY-MM-DD');
    const startStr = event.date;
    const endStr = event.end || event.date;
    return dayStr >= startStr && dayStr <= endStr;
  };

  const getEventIcon = (type: EventType) => {
    switch (type) {
      case 'birthday':
        return <BirthdayIcon />;
      case 'leave':
        return <GeneralLeaveIcon />;
      case 'public-holiday':
        return <PublicHolidayIcon />;
      default:
        return null;
    }
  };

  return (
    <CalendarWrapper>
      <CalendarContainer>
        <HeaderRow>
          <MonthNavigation>
            <NavButton onClick={handlePrevMonth} size="small">
              <ChevronLeftRounded />
            </NavButton>
            <MonthLabel>{currentMonth.format('MMMM YYYY')}</MonthLabel>
            <NavButton onClick={handleNextMonth} size="small">
              <ChevronRightRounded />
            </NavButton>
          </MonthNavigation>
          <Box style={{ flexGrow: 1 }} />
          <Box display="flex" alignItems="center" style={{ gap: '12px' }}>
            <TodayButton onClick={handleToday} variant="outlined">
              {t('today')}
            </TodayButton>
            <MinimizeButton aria-label={t('minimize')} onClick={onMinimize} size="small">
              <MinimizeIcon />
            </MinimizeButton>
          </Box>
        </HeaderRow>

        <WeekdaysGrid>
          {weekdays.map(day => (
            <WeekdayCell key={day}>{day}</WeekdayCell>
          ))}
        </WeekdaysGrid>

        <DaysGrid>
          {weeks.flatMap(week =>
            week.map(dayMoment => {
              const isCurrentMonth = dayMoment.month() === currentMonth.month();
              const isToday = dayMoment.isSame(moment(), 'day');
              const dayEvents = events.filter(e => isDayInEvent(dayMoment, e));

              return (
                <DayCell
                  key={dayMoment.format('YYYY-MM-DD')}
                  isCurrentMonth={isCurrentMonth}
                  isToday={isToday}
                >
                  <DayHeader>
                    <DayNumber isToday={isToday}>{dayMoment.date()}</DayNumber>
                  </DayHeader>
                  <EventsList>
                    {dayEvents.map(event => (
                      <EventPill
                        key={`${event.name}-${event.date}-${event.type}`}
                        eventType={event.type}
                        title={`${event.name} (${
                          event.end && event.end !== event.date
                            ? `${moment(event.date).format('D MMM')} - ${moment(
                                event.end
                              ).format('D MMM')}`
                            : moment(event.date).format('D MMM')
                        })`}
                      >
                        {getEventIcon(event.type)}
                        {event.name}
                      </EventPill>
                    ))}
                  </EventsList>
                </DayCell>
              );
            })
          )}
        </DaysGrid>
      </CalendarContainer>
    </CalendarWrapper>
  );
}
